import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { gsap } from 'gsap';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Zoom, Keyboard } from 'swiper';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/zoom';

import Cover from '../../assets/img/website-images/ganesha-visarjana-2026-cover.webp';
import Banner1 from '../../assets/img/website-images/ganesha-visarjana-2026-banner-1.webp';
import Banner2 from '../../assets/img/website-images/ganesha-visarjana-2026-banner-2.webp';
import Banner3 from '../../assets/img/website-images/ganesha-visarjana-2026-banner-3.webp';
import Banner4 from '../../assets/img/website-images/ganesha-visarjana-2026-banner-4.webp';
import Banner5 from '../../assets/img/website-images/ganesha-visarjana-2026-banner-5.webp';
import Banner6 from '../../assets/img/website-images/ganesha-visarjana-2026-banner-6.webp';
import Banner7 from '../../assets/img/website-images/ganesha-visarjana-2026-banner-7.webp';
import Banner8 from '../../assets/img/website-images/ganesha-visarjana-2026-banner-8.webp';
import Banner9 from '../../assets/img/website-images/ganesha-visarjana-2026-banner-9.webp';
import Banner10 from '../../assets/img/website-images/ganesha-visarjana-2026-banner-10.webp';
import Banner11 from '../../assets/img/website-images/ganesha-visarjana-2026-banner-11.webp';
import Banner12 from '../../assets/img/website-images/ganesha-visarjana-2026-banner-12.webp';
import Banner13 from '../../assets/img/website-images/ganesha-visarjana-2026-banner-13.webp';
import Banner14 from '../../assets/img/website-images/ganesha-visarjana-2026-banner-14.webp';
import Banner15 from '../../assets/img/website-images/ganesha-visarjana-2026-banner-15.webp';
import Banner16 from '../../assets/img/website-images/ganesha-visarjana-2026-banner-16.webp';
import Banner17 from '../../assets/img/website-images/ganesha-visarjana-2026-banner-17.webp';

const galleryImages = [
    Banner1, Banner2, Banner3, Banner4, Banner5, Banner6, Banner7, Banner8, Banner9,
    Banner10, Banner11, Banner12, Banner13, Banner14, Banner15, Banner16, Banner17,
];

/* ─── Variants ─────────────────────────────────────────────────── */
const heroVariants = {
    hidden: { opacity: 0, scale: 1.04 },
    visible: {
        opacity: 1, scale: 1,
        transition: { duration: 1.0, ease: [0.22, 1, 0.36, 1] },
    },
};

const titleVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
        opacity: 1, y: 0,
        transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.15 },
    },
};

const tileGridVariants = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.06 } },
};

const tileVariants = {
    hidden: { opacity: 0, y: 24, scale: 0.96 },
    visible: {
        opacity: 1, y: 0, scale: 1,
        transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
    },
};

/* ─── Paragraph with individual scroll trigger ──────────────────── */
function AnimPara({ children, delay = 0 }) {
    const { ref, inView } = useInView({ threshold: 0.2, triggerOnce: true });
    return (
        <motion.p
            ref={ref}
            initial={{ opacity: 0, y: 28 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.65, ease: 'easeOut', delay }}
        >
            {children}
        </motion.p>
    );
}

/* ─── Gallery Tile ───────────────────────────────────────────────── */
function GalleryTile({ src, index, onOpen }) {
    const imgRef = useRef(null);

    const handleMouseEnter = () => {
        gsap.to(imgRef.current, { scale: 1.08, duration: 0.5, ease: 'power2.out' });
    };
    const handleMouseLeave = () => {
        gsap.to(imgRef.current, { scale: 1, duration: 0.5, ease: 'power2.out' });
    };

    return (
        <motion.div
            className="col-lg-3 col-md-4 col-6"
            variants={tileVariants}
            style={{ marginBottom: '24px' }}
        >
            <div
                onClick={() => onOpen(index)}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                style={{
                    aspectRatio: '1 / 1',
                    overflow: 'hidden',
                    borderRadius: '14px',
                    cursor: 'pointer',
                    position: 'relative',
                    boxShadow: '0 6px 18px rgba(30, 90, 168, 0.12)',
                }}
            >
                <img
                    ref={imgRef}
                    src={src}
                    alt={`Ganesha Visarjana 2026 photo ${index + 1}`}
                    loading="lazy"
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
            </div>
        </motion.div>
    );
}

/* ─── Main Component ───────────────────────────────────────────── */
function GaneshaVisarjana() {
    const { ref: heroRef, inView: heroInView } = useInView({ threshold: 0.2, triggerOnce: true });
    const { ref: titleRef, inView: titleInView } = useInView({ threshold: 0.2, triggerOnce: true });
    const { ref: gridRef, inView: gridInView } = useInView({ threshold: 0.1, triggerOnce: true });

    const [isOpen, setIsOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);

    const openGallery = (index) => {
        setActiveIndex(index);
        setIsOpen(true);
    };
    const closeGallery = () => setIsOpen(false);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && isOpen) closeGallery();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen]);

    const imgWrapRef = useRef(null);
    useEffect(() => {
        if (heroInView && imgWrapRef.current) {
            gsap.fromTo(
                imgWrapRef.current,
                { clipPath: 'inset(0 100% 0 0)', opacity: 1 },
                { clipPath: 'inset(0 0% 0 0)', duration: 1.1, ease: 'power3.inOut', delay: 0.1 }
            );
        }
    }, [heroInView]);

    return (
        <>
            <section className="project-detail">
                <div className="container">

                    {/* ── Hero Image ── */}
                    <div className="upper-box" ref={heroRef}>
                        <motion.div
                            variants={heroVariants}
                            initial="hidden"
                            animate={heroInView ? 'visible' : 'hidden'}
                            style={{ borderRadius: '12px', overflow: 'hidden' }}
                        >
                            <div ref={imgWrapRef} style={{ overflow: 'hidden', borderRadius: '12px' }}>
                                <figure className="image" style={{ margin: 0, overflow: 'hidden' }}>
                                    <img
                                        src={Cover}
                                        alt="Ganesha Visarjana 2026"
                                        style={{ display: 'block', width: '100%' }}
                                    />
                                </figure>
                            </div>
                        </motion.div>
                    </div>

                    {/* ── Article Content ── */}
                    <div className="lower-content2">
                        <div className="row">
                            <div className="text-column col-lg-12 col-md-12 col-sm-12">
                                <div className="s-about-content">

                                    <motion.h2
                                        ref={titleRef}
                                        variants={titleVariants}
                                        initial="hidden"
                                        animate={titleInView ? 'visible' : 'hidden'}
                                    >
                                        Ganesha Visarjana 2026 — A Colourful Farewell to Lord Ganesha at Akash Campus
                                    </motion.h2>

                                    <AnimPara delay={0}>
                                        The Akash campus came alive with devotion, colour, and community spirit as
                                        students, staff, and families gathered to celebrate Ganesha Visarjana 2026.
                                        A beautifully decorated idol of Lord Ganesha was taken through the campus in
                                        a joyous procession, with everyone coming together to sing, dance, and bid a
                                        heartfelt farewell.
                                    </AnimPara>

                                    <AnimPara delay={0.1}>
                                        From a beautifully decorated mandap to a vibrant procession through the
                                        campus, the celebrations saw enthusiastic participation from students,
                                        faculty, and staff across Akash Institute of Pharmacy and the wider Akash
                                        Group of Institutions. The festivities were filled with traditional
                                        rituals, music, and dance, bringing together people of all ages in shared
                                        devotion and joy.
                                    </AnimPara>

                                    <AnimPara delay={0.2}>
                                        Moments like these remind us that AIP isn't just a place of learning — it's
                                        a community that comes together in celebration, tradition, and togetherness.
                                    </AnimPara>

                                    <AnimPara delay={0.3}>
                                        <strong>Ganapati Bappa Morya! 🙏</strong>
                                    </AnimPara>

                                </div>
                            </div>
                        </div>
                    </div>

                    {/* ── Photo Gallery ── */}
                    <div className="lower-content2" style={{ paddingTop: 0 }}>
                        <div className="row">
                            <div className="col-lg-12">
                                <h3 style={{ marginBottom: '30px' }}>Event Gallery</h3>
                            </div>
                        </div>
                        <motion.div
                            ref={gridRef}
                            className="row"
                            variants={tileGridVariants}
                            initial="hidden"
                            animate={gridInView ? 'visible' : 'hidden'}
                        >
                            {galleryImages.map((src, index) => (
                                <GalleryTile key={index} src={src} index={index} onOpen={openGallery} />
                            ))}
                        </motion.div>
                    </div>

                </div>
            </section>

            {/* ── Lightbox Modal ── */}
            {isOpen && (
                <div
                    onClick={closeGallery}
                    style={{
                        position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
                        background: 'rgba(10, 20, 35, 0.95)', zIndex: 9999,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                >
                    <button
                        onClick={closeGallery}
                        aria-label="Close gallery"
                        style={{
                            position: 'absolute', top: 24, right: 32, fontSize: 32, color: '#fff',
                            background: 'transparent', border: 'none', cursor: 'pointer', zIndex: 10000,
                        }}
                    >
                        &times;
                    </button>
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{ width: '90%', height: '85%', maxWidth: 1100 }}
                    >
                        <Swiper
                            modules={[Navigation, Pagination, Zoom, Keyboard]}
                            navigation
                            pagination={{ clickable: true }}
                            zoom={true}
                            keyboard={{ enabled: true }}
                            initialSlide={activeIndex}
                            spaceBetween={20}
                            style={{ width: '100%', height: '100%' }}
                        >
                            {galleryImages.map((src, index) => (
                                <SwiperSlide key={index}>
                                    <div className="swiper-zoom-container">
                                        <img
                                            src={src}
                                            alt={`Ganesha Visarjana 2026 photo ${index + 1}`}
                                            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                        />
                                    </div>
                                </SwiperSlide>
                            ))}
                        </Swiper>
                    </div>
                </div>
            )}
        </>
    );
}

export default GaneshaVisarjana;
